/**
 * Asistente conversacional para cargar el MFA de una credencial compartida
 * (Sol 28/09/2026: "un chat interactivo con un agente que ayude a configurarlo,
 * que te puedan pasar captura, pero aclará que solo exporten su cuenta y que te
 * digan si coincide después; lo más amigable posible").
 *
 * Es una máquina de estados que dibuja el FRONT (respuestas rápidas), no algo
 * que orquesta el modelo:
 *   where ─┬─ google ─ captura del QR de "Exportar cuentas" ─┐
 *          ├─ other  ─ clave nueva en la plataforma (pegar) ──┼─ pick (si vinieron varias) ─ guardar ─ verify ─ done
 *          └─ none   ─ activar 2FA en la plataforma (pegar) ─┘
 * Las preguntas libres van a Haiku (`vault.mfaAssistantAsk`), sin la imagen ni
 * la clave. La captura se decodifica en el navegador (`lib/qr-decode.ts` +
 * `shared/otpauth-migration.ts`) y no se muestra ni se sube. La semilla se
 * guarda con `vault.updateSecret` (la misma mutation que Editar) y el código
 * que genera el Vault se compara con el teléfono: "¿Coincide?". Si no coincide
 * y la credencial no tenía MFA, se quita para que nadie confíe en un código
 * equivocado. La conversación no se persiste (mismo criterio que el chat de TA).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlus, Sparkles } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChatBubble, ThinkingBubble } from '@/components/chat/ChatBubble';
import { ChatComposer } from '@/components/chat/ChatComposer';
import { QuickReplies, type QuickReplyOption } from '@/components/chat/QuickReplies';
import { useChatScroll } from '@/components/chat/useChatScroll';
import { BrainMarkdown } from '@/components/brain/BrainMarkdown';
import { TotpField } from '@/components/vault/TotpField';
import { errMsg, isVaultLockedError, withUnlockRetry } from '@/hooks/useVault';
import { decodeQrFromImage, imageFromDataTransfer } from '@/lib/qr-decode';
import { cn } from '@/lib/utils';
import { normalizeLang } from '../../../../shared/lang';
import { looksLikeOtpSecret, otpAccountLabel, parseOtpQr, type OtpAccount } from '../../../../shared/otpauth-migration';
import type { MfaAssistantStep } from '../../../../shared/types/vault';

type Step = MfaAssistantStep;
type Origin = 'google' | 'other' | 'none';

interface Msg {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  /** `capture`: la persona pasó una imagen (no se muestra). `totp`: burbuja con el código del Vault para comparar. */
  kind?: 'text' | 'capture' | 'totp';
  totp?: { digits: number; step: number };
}

interface MfaAssistantProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  secretId: string;
  secretName: string;
  version: number;
  hasTotp: boolean;
  ensureUnlocked: () => Promise<boolean>;
}

const PLATFORMS = ['meta', 'google_platform', 'tiktok', 'linkedin', 'other_platform'] as const;
const K = 'vault.mfa_assistant';

export function MfaAssistant({ open, onOpenChange, secretId, secretName, version, hasTotp, ensureUnlocked }: MfaAssistantProps) {
  const { t, i18n } = useTranslation();
  const utils = trpc.useUtils();
  const ask = trpc.vault.mfaAssistantAsk.useMutation();
  const update = trpc.vault.updateSecret.useMutation({
    onSuccess: () => {
      utils.vault.getSecret.invalidate({ secretId });
      utils.vault.listSecrets.invalidate();
    },
  });

  const [messages, setMessages] = useState<Msg[]>([]);
  const [replies, setReplies] = useState<QuickReplyOption[]>([]);
  const [step, setStep] = useState<Step>('where');
  const [input, setInput] = useState('');
  const [busy, setBusyState] = useState<null | 'thinking' | 'reading' | 'saving'>(null);
  const [dragging, setDragging] = useState(false);
  // Guardia SÍNCRONA además del estado: dos drops/clicks en el mismo tick veían `busy` viejo
  // (el workshop soltó la captura dos veces y se procesó dos veces).
  const lock = useRef(false);
  const setBusy = (v: null | 'thinking' | 'reading' | 'saving') => {
    lock.current = v !== null;
    setBusyState(v);
  };
  const origin = useRef<Origin>('google');
  const versionRef = useRef(version);
  const hadTotpBefore = useRef(hasTotp);
  const pendingAccount = useRef<OtpAccount | null>(null);
  const pickList = useRef<OtpAccount[]>([]);
  const nextId = useRef(1);
  const fileRef = useRef<HTMLInputElement>(null);
  const endRef = useChatScroll([messages, replies, busy]);
  const lang = normalizeLang(i18n.language);
  const name = secretName;

  const opt = (id: string, tone?: QuickReplyOption['tone']): QuickReplyOption => ({ id, label: t(`${K}.reply.${id}`), tone });
  const say = (text: string, next: QuickReplyOption[] = [], extra: Partial<Msg> = {}) => {
    setMessages((m) => [...m, { id: nextId.current++, role: 'assistant', text, ...extra }]);
    setReplies(next);
  };
  const userSay = (text: string, kind: Msg['kind'] = 'text') => {
    setMessages((m) => [...m, { id: nextId.current++, role: 'user', text, kind }]);
    setReplies([]);
  };

  const whereReplies = () => [opt('google', 'primary'), opt('other'), opt('none'), opt('unknown')];
  const platformReplies = () => [...PLATFORMS.map((p) => opt(p)), opt('paste_key')];
  const stepReplies = (s: Step): QuickReplyOption[] => {
    if (s === 'where') return whereReplies();
    if (s === 'google') return [opt('no_export'), opt('have_capture', 'primary')];
    if (s === 'other' || s === 'none') return platformReplies();
    if (s === 'verify') return [opt('match_yes', 'primary'), opt('match_no')];
    if (s === 'done') return [opt('close', 'primary')];
    return [];
  };

  // Arranque limpio en cada apertura: nada se persiste.
  useEffect(() => {
    if (!open) return;
    setMessages([]);
    setInput('');
    setBusy(null);
    setStep('where');
    versionRef.current = version;
    hadTotpBefore.current = hasTotp;
    pendingAccount.current = null;
    pickList.current = [];
    if (hasTotp) say(t(`${K}.msg.already`, { name }), [opt('continue_replace', 'primary'), opt('cancel')]);
    else say(t(`${K}.msg.hello`, { name }), whereReplies());
    // solo al abrir: `say`/`opt` cambian en cada render y no tienen que reiniciar la conversación
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const goto = (s: Origin) => {
    origin.current = s;
    setStep(s);
    if (s === 'google') say(t(`${K}.msg.google`, { name }), stepReplies('google'));
    else if (s === 'other') say(t(`${K}.msg.other`, { name }), platformReplies());
    else say(t(`${K}.msg.none`, { name }), platformReplies());
  };

  const seedOf = (a: OtpAccount) =>
    a.digits === 6 && a.algorithm === 'SHA1' && a.period === 30
      ? a.secret
      : `otpauth://totp/${encodeURIComponent(a.issuer || 'Vault')}?secret=${a.secret}&digits=${a.digits}&algorithm=${a.algorithm}&period=${a.period}`;

  const save = async (a: OtpAccount) => {
    pendingAccount.current = a;
    setBusy('saving');
    try {
      const res = await withUnlockRetry(ensureUnlocked, () => update.mutateAsync({ secretId, version: versionRef.current, totpSeed: seedOf(a) }));
      if (!res) {
        say(t(`${K}.msg.unlock_needed`), [opt('save_again', 'primary')]);
        return;
      }
      versionRef.current = res.version;
      setStep('verify');
      const label = otpAccountLabel(a);
      say(origin.current === 'google' ? t(`${K}.msg.verify_phone`, { name, label }) : t(`${K}.msg.verify_platform`, { name }), stepReplies('verify'), {
        kind: 'totp',
        totp: { digits: a.digits, step: a.period },
      });
    } catch (e) {
      if (!isVaultLockedError(e as { message?: string })) say(t(`${K}.msg.save_error`, { msg: errMsg(e as { message?: string }) }), [opt('save_again', 'primary'), opt('retry')]);
    } finally {
      setBusy(null);
    }
  };

  const confirm = (a: OtpAccount, kind: 'setup' | 'migration') => {
    pendingAccount.current = a;
    const label = otpAccountLabel(a);
    say(kind === 'setup' ? t(`${K}.msg.confirm_setup`, { label, name }) : t(`${K}.msg.confirm_export`, { label, name }), [opt('save_yes', 'primary'), opt('save_no')]);
  };

  const handleAccounts = (kind: 'setup' | 'migration', accounts: OtpAccount[]) => {
    const usable = accounts.filter((a) => a.type === 'TOTP' && a.algorithm !== 'MD5');
    if (!usable.length) {
      say(t(`${K}.msg.unsupported`), stepReplies(origin.current));
      return;
    }
    if (usable.length === 1) {
      confirm(usable[0], kind);
      return;
    }
    pickList.current = usable;
    setStep('pick');
    say(t(`${K}.msg.pick`, { n: usable.length, name }), [...usable.map((a, i) => ({ id: `acc-${i}`, label: otpAccountLabel(a) })), opt('none_of_these')]);
  };

  const handleKeyText = (text: string) => {
    userSay(t(`${K}.msg.key_received`));
    const parsed = parseOtpQr(text);
    if (parsed.kind === 'corrupt') {
      say(t(`${K}.msg.qr_corrupt`), stepReplies(origin.current));
      return;
    }
    if (parsed.kind !== 'unknown') {
      handleAccounts(parsed.kind, parsed.accounts);
      return;
    }
    void save({ issuer: '', name: '', secret: text.toUpperCase().replace(/[\s\-=]/g, ''), digits: 6, algorithm: 'SHA1', type: 'TOTP', period: 30 });
  };

  const handleImage = async (file: File) => {
    if (lock.current) return;
    userSay(t(`${K}.msg.capture_received`), 'capture');
    setBusy('reading');
    let raw: string | null = null;
    try {
      raw = await decodeQrFromImage(file);
    } catch {
      raw = null;
    } finally {
      setBusy(null);
    }
    if (!raw) {
      say(t(`${K}.msg.no_qr`), stepReplies(origin.current));
      return;
    }
    const parsed = parseOtpQr(raw);
    if (parsed.kind === 'unknown') {
      say(t(`${K}.msg.not_auth_qr`), stepReplies(origin.current));
      return;
    }
    if (parsed.kind === 'corrupt') {
      say(t(`${K}.msg.qr_corrupt`), stepReplies(origin.current));
      return;
    }
    if (!parsed.accounts.length) {
      say(t(`${K}.msg.qr_empty`), stepReplies(origin.current));
      return;
    }
    handleAccounts(parsed.kind, parsed.accounts);
  };

  const askModel = async (question: string) => {
    userSay(question);
    setBusy('thinking');
    try {
      const history = messages.filter((m) => m.kind !== 'capture').slice(-10).map((m) => ({ role: m.role, text: m.text }));
      const { answer } = await ask.mutateAsync({ secretId, step, question, history, lang });
      say(answer || t(`${K}.msg.ask_error`), stepReplies(step));
    } catch {
      say(t(`${K}.msg.ask_error`), stepReplies(step));
    } finally {
      setBusy(null);
    }
  };

  const send = () => {
    const text = input.trim();
    if (!text || lock.current) return;
    setInput('');
    if (looksLikeOtpSecret(text)) handleKeyText(text);
    else void askModel(text);
  };

  // "No coincide" ⇒ se quita SIEMPRE (code-reviewer 28/09): si la credencial ya tenía un MFA,
  // la clave anterior fue reemplazada al guardar y no hay copia; dejar la nueva —sabida incorrecta—
  // con el badge "TOTP" sería peor que "Sin MFA".
  const mismatch = async () => {
    const back = origin.current;
    setBusy('saving');
    try {
      const res = await withUnlockRetry(ensureUnlocked, () => update.mutateAsync({ secretId, version: versionRef.current, totpSeed: null }));
      if (res) {
        versionRef.current = res.version;
        say(t(hadTotpBefore.current ? `${K}.msg.mismatch_replaced` : `${K}.msg.mismatch_removed`, { name }), [opt('retry', 'primary')]);
        hadTotpBefore.current = false;
      } else {
        say(t(`${K}.msg.mismatch_not_removed`), [opt('retry', 'primary')]);
      }
    } catch (e) {
      if (!isVaultLockedError(e as { message?: string })) say(t(`${K}.msg.save_error`, { msg: errMsg(e as { message?: string }) }), [opt('retry', 'primary')]);
    } finally {
      setBusy(null);
    }
    setStep(back);
  };

  const pick = (id: string) => {
    if (lock.current) return;
    if (id.startsWith('acc-')) {
      const a = pickList.current[Number(id.slice(4))];
      if (!a) return;
      userSay(otpAccountLabel(a));
      void save(a);
      return;
    }
    if ((PLATFORMS as readonly string[]).includes(id)) {
      void askModel(t(`${K}.question.${id}`));
      return;
    }
    userSay(t(`${K}.reply.${id}`));
    switch (id) {
      case 'continue_replace':
        say(t(`${K}.msg.hello`, { name }), whereReplies());
        break;
      case 'cancel':
      case 'close':
        onOpenChange(false);
        break;
      case 'google':
      case 'other':
      case 'none':
        goto(id);
        break;
      case 'unknown':
        say(t(`${K}.msg.unknown`), [opt('google', 'primary'), opt('other'), opt('none')]);
        break;
      case 'no_export':
        say(t(`${K}.msg.no_export`), [opt('have_capture', 'primary'), opt('is_other_app')]);
        break;
      case 'is_other_app':
        goto('other');
        break;
      case 'have_capture':
        say(t(`${K}.msg.capture_prompt`), []);
        break;
      case 'paste_key':
        say(t(`${K}.msg.paste_key_prompt`), []);
        break;
      case 'none_of_these':
        setStep(origin.current);
        say(t(`${K}.msg.none_of_these`, { name }), [opt('retry', 'primary')]);
        break;
      case 'save_yes':
        if (pendingAccount.current) void save(pendingAccount.current);
        break;
      case 'save_again':
        if (pendingAccount.current) void save(pendingAccount.current);
        break;
      case 'save_no':
        say(t(`${K}.msg.wrong_account`, { name }), [opt('retry', 'primary')]);
        break;
      case 'match_yes':
        setStep('done');
        say(t(`${K}.msg.done`, { name }), stepReplies('done'));
        break;
      case 'match_no':
        void mismatch();
        break;
      case 'retry':
        goto(origin.current);
        break;
    }
  };

  const busyLabel = busy === 'thinking' ? t(`${K}.thinking`) : busy === 'reading' ? t(`${K}.reading`) : busy === 'saving' ? t(`${K}.saving`) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl"
        data-tour="mfa-chat"
        onPaste={(e) => {
          const f = imageFromDataTransfer(e.clipboardData);
          if (f) {
            e.preventDefault();
            void handleImage(f);
          }
        }}
      >
        <DialogHeader className="border-b px-5 py-4 text-left">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand-green" />
            {t(`${K}.title`)}
          </DialogTitle>
          <DialogDescription>{t(`${K}.subtitle`, { name })}</DialogDescription>
        </DialogHeader>

        <div
          className={cn('relative min-h-[320px] flex-1 space-y-3 overflow-y-auto px-5 py-4', dragging && 'bg-brand-green/5')}
          data-tour="mfa-drop"
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const f = imageFromDataTransfer(e.dataTransfer);
            if (f) void handleImage(f);
          }}
        >
          {messages.map((m) => (
            <ChatBubble key={m.id} role={m.role}>
              {m.role === 'assistant' ? <BrainMarkdown content={m.text} /> : <span className={m.kind === 'capture' ? 'italic' : undefined}>{m.text}</span>}
              {m.kind === 'totp' && m.totp && (
                <div className="mt-3 rounded-md border bg-background p-3">
                  <TotpField secretId={secretId} digits={m.totp.digits} step={m.totp.step} ensureUnlocked={ensureUnlocked} autoStart />
                </div>
              )}
            </ChatBubble>
          ))}
          {busyLabel && <ThinkingBubble label={busyLabel} />}
          {!busy && replies.length > 0 && <QuickReplies options={replies} onPick={pick} tour="mfa" className="pt-1" />}
          {dragging && (
            <div className="pointer-events-none absolute inset-2 flex items-center justify-center rounded-lg border-2 border-dashed border-brand-green bg-background/80 text-sm font-medium">
              {t(`${K}.drop_here`)}
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t px-5 py-3">
          <div className="flex gap-2" data-tour="mfa-composer">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) void handleImage(f);
              }}
            />
            <Button type="button" variant="outline" size="icon" onClick={() => fileRef.current?.click()} disabled={!!busy} title={t(`${K}.attach`)} aria-label={t(`${K}.attach`)} data-tour="mfa-attach">
              <ImagePlus className="h-4 w-4" />
            </Button>
            <ChatComposer value={input} onChange={setInput} onSend={send} placeholder={t(`${K}.placeholder`)} maxLength={1000} disabled={!!busy} className="flex-1" />
          </div>
          <p className="mt-2 text-[11px] leading-snug text-muted-foreground">{t(`${K}.privacy`)}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
